// ═══════════════════════════════════════════════════════════════════
// PATHS CONFIGURATION - Service paths for homepage triage portal
// ═══════════════════════════════════════════════════════════════════

export const pathsConfig = {
  speaking: {
    id: 'speaking',
    title: 'I need a speaker for an event',
    description: 'Keynotes and inspirational talks that transform how audiences think about creativity and innovation',
    color: 'purple',
    colorHex: '#9333EA',
    borderColor: 'border-purple-500',
    bgHover: 'hover:bg-purple-500/10',
    icon: '🎤', // Using emoji for MVP, can replace with SVG later
    formFields: [
      {
        id: 'event_type',
        type: 'select',
        label: 'What type of event?',
        options: ['Conference', 'Corporate Event', 'Educational', 'Workshop', 'Other'],
        required: true
      },
      {
        id: 'event_date',
        type: 'date',
        label: 'When is your event?',
        required: true
      },
      {
        id: 'event_location',
        type: 'text',
        label: 'Where is it happening?',
        placeholder: 'City, State or Virtual',
        required: true
      },
      {
        id: 'audience_size',
        type: 'select',
        label: 'Expected audience size?',
        options: ['< 50', '50-100', '100-500', '500-1000', '1000+'],
        required: true
      },
      {
        id: 'talk_topics',
        type: 'textarea',
        label: 'What topics or themes interest you?',
        placeholder: 'Innovation, creativity, impossible ideas, etc.',
        required: true
      },
      {
        id: 'budget_range',
        type: 'select',
        label: 'Budget range?',
        options: ['< $5K', '$5K-$10K', '$10K-$25K', '$25K+', 'Flexible'],
        required: false
      }
    ]
  },

  workshops: {
    id: 'workshops',
    title: 'I want to build innovation skills in my team',
    description: 'Hands-on workshops that develop creative capacity and collaborative problem-solving',
    color: 'yellow',
    colorHex: '#FFB100',
    borderColor: 'border-yellow-500',
    bgHover: 'hover:bg-yellow-500/10',
    icon: '👥',
    formFields: [
      {
        id: 'team_size',
        type: 'text',
        label: 'How many people on your team?',
        placeholder: 'e.g., 10-15 people',
        required: true
      },
      {
        id: 'team_type',
        type: 'text',
        label: 'What kind of team?',
        placeholder: 'Engineering, Design, Leadership, Mixed, etc.',
        required: true
      },
      {
        id: 'workshop_duration',
        type: 'select',
        label: 'How long should the workshop be?',
        options: ['Half day', 'Full day', 'Multi-day', 'Ongoing series'],
        required: true
      },
      {
        id: 'skills_focus',
        type: 'textarea',
        label: 'What skills or challenges do you want to address?',
        placeholder: 'Creative problem-solving, prototyping, collaboration, etc.',
        required: true
      },
      {
        id: 'timeline',
        type: 'text',
        label: 'When would you like to do this?',
        placeholder: 'Q2 2024, Next month, etc.',
        required: true
      },
      {
        id: 'location',
        type: 'text',
        label: 'Where would this happen?',
        placeholder: 'Our office in NYC, Virtual, etc.',
        required: true
      }
    ]
  },

  education: {
    id: 'education',
    title: "I'm looking for programs for students/educators",
    description: 'Educational experiences that ignite curiosity through hands-on learning and creative discovery',
    color: 'blue',
    colorHex: '#0EA5E9',
    borderColor: 'border-blue-500',
    bgHover: 'hover:bg-blue-500/10',
    icon: '📚',
    formFields: [
      {
        id: 'student_age',
        type: 'select',
        label: 'What age group?',
        options: ['Elementary (K-5)', 'Middle School (6-8)', 'High School (9-12)', 'College/University', 'Mixed ages'],
        required: true
      },
      {
        id: 'num_students',
        type: 'text',
        label: 'How many students?',
        placeholder: 'e.g., 20-25 students',
        required: true
      },
      {
        id: 'program_type',
        type: 'select',
        label: 'What type of program?',
        options: ['After-school', 'Summer camp', 'Semester course', 'One-time workshop', 'Multi-session series'],
        required: true
      },
      {
        id: 'learning_goals',
        type: 'textarea',
        label: 'What are your learning objectives?',
        placeholder: 'STEAM skills, creative thinking, problem-solving, etc.',
        required: true
      },
      {
        id: 'resources',
        type: 'textarea',
        label: 'What resources do you have available?',
        placeholder: 'Maker space, 3D printers, computers, budget, etc.',
        required: false
      },
      {
        id: 'timeline',
        type: 'text',
        label: 'When would this program run?',
        placeholder: 'Fall 2024, Next semester, etc.',
        required: true
      }
    ]
  },

  projects: {
    id: 'projects',
    title: 'I have an idea I want to bring to life',
    description: 'Custom creative discovery projects that turn impossible questions into tangible realities',
    color: 'red',
    colorHex: '#EF4444',
    borderColor: 'border-red-500',
    bgHover: 'hover:bg-red-500/10',
    icon: '💡',
    formFields: [
      {
        id: 'project_description',
        type: 'textarea',
        label: "What's your impossible idea?",
        placeholder: 'Describe your vision in as much detail as you can...',
        required: true
      },
      {
        id: 'why_impossible',
        type: 'textarea',
        label: 'What makes it feel impossible?',
        placeholder: 'Technical challenges, scale, materials, physics, etc.',
        required: true
      },
      {
        id: 'timeline',
        type: 'select',
        label: 'When do you need it?',
        options: ['ASAP', 'Next month', 'Next quarter', 'This year', 'Flexible'],
        required: true
      },
      {
        id: 'budget',
        type: 'select',
        label: 'Budget range?',
        options: ['< $10K', '$10K-$50K', '$50K-$100K', '$100K+', "Let's discuss"],
        required: false
      },
      {
        id: 'success_criteria',
        type: 'textarea',
        label: 'What does success look like?',
        placeholder: 'Working prototype, public installation, proof of concept, etc.',
        required: true
      },
      {
        id: 'inspiration',
        type: 'text',
        label: 'Any reference or inspiration?',
        placeholder: 'Links, images, or descriptions welcome',
        required: false
      }
    ]
  },

  other: {
    id: 'other',
    title: 'Something else',
    description: 'Press inquiries, partnerships, store returns, or other questions',
    color: 'green',
    colorHex: '#10B981',
    borderColor: 'border-green-500',
    bgHover: 'hover:bg-green-500/10',
    icon: '❓',
    formFields: [
      {
        id: 'inquiry_type',
        type: 'select',
        label: 'What kind of inquiry?',
        options: ['Press/Media', 'Partnership', 'Store Return', 'Job/Career', 'General Question', 'Other'],
        required: true
      },
      {
        id: 'message',
        type: 'textarea',
        label: 'Tell us more',
        placeholder: 'Please provide as much detail as possible...',
        required: true
      },
      {
        id: 'urgency',
        type: 'select',
        label: 'How urgent is this?',
        options: ['Not urgent', 'Within a week', 'Within 48 hours', 'Very urgent'],
        required: false
      },
      {
        id: 'preferred_contact',
        type: 'select',
        label: 'Preferred contact method?',
        options: ['Email', 'Phone', 'Either'],
        required: false
      }
    ]
  }
};

// Helper function to get all paths as array
export const getAllPaths = () => Object.values(pathsConfig);

// Helper function to get path by ID
export const getPathById = (id) => pathsConfig[id] || null;

// Form submission endpoint (can be updated based on backend choice)
export const FORM_SUBMIT_URL = 'https://formsubmit.co/hello@budmen.com';

// Typeform IDs (if using Typeform integration)
export const typeformIds = {
  speaking: 'abc123', // Replace with actual Typeform IDs
  workshops: 'def456',
  education: 'ghi789',
  projects: 'jkl012',
  other: 'mno345'
};